import React, { useState, useEffect, useRef } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, doc, writeBatch, serverTimestamp } from "firebase/firestore";
import { Call, Message, Calendar, SearchNormal1, People } from "iconsax-reactjs";
import { consolidateLeads } from "../utils/leadUtils";
import { queueLeadChat } from "../utils/chatShared";
import { getTypeLabel, getListingBadge } from "../shared/listingRules";
import Select from "./Select";
import "./DealerLeads.css";

const LEAD_STATUSES = [
  "New",
  "Contacted",
  "Qualified",
  "Visit Scheduled",
  "Converted",
  "Closed"
];

// Chip tone per status: a fresh lead is emerald with a dot, a scheduled visit
// needs attention (amber), a closed lead recedes. Unknown or legacy values
// fall back to the neutral chip.
const STATUS_TONES = {
  New: "chip-success chip-dot",
  Contacted: "",
  Qualified: "chip-primary",
  "Visit Scheduled": "chip-warning",
  Converted: "chip-success",
  Closed: "lead-chip-quiet",
};

const CHANNEL_LABELS = {
  call: "Call",
  chat: "Chat",
  visit: "Visit",
  enquiry: "Enquiry",
  inquiry: "Enquiry",
};

const SKELETON_ROWS = [0, 1, 2, 3, 4];

/** Display label for a stored status value ("Visit Scheduled" reads "Visit scheduled"). */
export function leadStatusLabel(status) {
  const s = status || "New";
  return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
}

const STATUS_FILTERS = [
  { value: "all", label: "All statuses" },
  ...LEAD_STATUSES.map((status) => ({ value: status, label: leadStatusLabel(status) })),
];

// The row's status menu: every stage, with the lead's own one marked and
// not pickable.
const statusChoices = (current) =>
  LEAD_STATUSES.map((status) => ({
    value: status,
    label: leadStatusLabel(status),
    disabled: status === current,
    hint: status === current ? "Current" : undefined,
  }));

export function LeadStatusChip({ status }) {
  const s = status || "New";
  const tone = STATUS_TONES[s] || "";
  return <span className={`chip ${tone}`.trim()}>{leadStatusLabel(s)}</span>;
}

export function LeadChannelChip({ type }) {
  const t = type || "enquiry";
  const label = CHANNEL_LABELS[t] || t.charAt(0).toUpperCase() + t.slice(1);
  return (
    <span className="chip">
      {t === "call" && <Call size={12} variant="Linear" aria-hidden="true" />}
      {t === "chat" && <Message size={12} variant="Linear" aria-hidden="true" />}
      {t !== "call" && t !== "chat" && <Calendar size={12} variant="Linear" aria-hidden="true" />}
      {label}
    </span>
  );
}

/** Latest activity on a consolidated lead, as a Date (null while unknown). */
export function leadActivityDate(lead) {
  const ts = lead.updatedAt || lead.createdAt;
  return ts?.seconds ? new Date(ts.seconds * 1000) : null;
}

function formatRelative(date) {
  const minutes = Math.floor((Date.now() - date.getTime()) / 60000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 7) return `${days} days ago`;
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString([], { day: "numeric", month: "short", year: sameYear ? undefined : "numeric" });
}

function formatExact(date) {
  return date.toLocaleString([], { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

/** Relative time ("3 h ago") with the exact date and time on hover. */
export function LeadTime({ date, fallback = "Recently" }) {
  if (!date) return fallback ? <span className="muted">{fallback}</span> : null;
  return (
    <time className="lead-time" dateTime={date.toISOString()} title={formatExact(date)}>
      {formatRelative(date)}
    </time>
  );
}

export default function DealerLeads({ user, setActiveTab: setParentActiveTab, focusLead = null }) {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("direct"); // "direct" or "nearby"
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const handledFocusRef = useRef(null);

  // A lead opened from a notification: show its tab without filters that
  // could hide it, then bring its row into view and light it for a moment.
  useEffect(() => {
    if (!focusLead) return;
    setActiveTab(focusLead.view === "nearby" ? "nearby" : "direct");
    setStatusFilter("all");
    setSearchQuery("");
  }, [focusLead]);

  // Notifications name the raw lead doc; rows are consolidated leads.
  const focusedLeadId = focusLead
    ? leads.find((l) => l.id === focusLead.leadId || (l.sourceIds || []).includes(focusLead.leadId))?.id
    : undefined;

  // The row only exists once the leads have loaded and the tab switch and
  // cleared filters have rendered, so this re-checks on each of those and
  // acts once per request.
  useEffect(() => {
    if (!focusedLeadId || handledFocusRef.current === focusLead.at) return;
    const row = document.getElementById(`lead-row-${focusedLeadId}`);
    if (!row) return;
    handledFocusRef.current = focusLead.at;
    setHighlightId(focusedLeadId);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    row.scrollIntoView({ block: "center", behavior: reduceMotion ? "auto" : "smooth" });
  }, [focusLead, focusedLeadId, loading, activeTab, statusFilter, searchQuery]);

  useEffect(() => {
    if (!highlightId) return undefined;
    const timer = setTimeout(() => setHighlightId(null), 4000);
    return () => clearTimeout(timer);
  }, [highlightId]);

  useEffect(() => {
    if (!user?.uid) return;

    const colLeads = collection(db, "users", user.uid, "leads");
    const unsubscribe = onSnapshot(colLeads, (snap) => {
      const list = [];
      snap.forEach((d) => list.push({ id: d.id, ...d.data() }));
      setLeads(consolidateLeads(list));
      setLoading(false);
    }, (err) => {
      console.error("Error subscribing to leads:", err);
      setLoading(false);
    });

    return () => unsubscribe();
  }, [user]);

  // A consolidated lead can span several legacy docs; update them all so the
  // merged status can't snap back to an older doc's value. updatedAt matches
  // the app's updateLeadStatus.
  const handleStatusChange = async (lead, newStatus) => {
    try {
      const batch = writeBatch(db);
      (lead.sourceIds || [lead.id]).forEach((id) => {
        batch.update(doc(db, "users", user.uid, "leads", id), {
          status: newStatus,
          updatedAt: serverTimestamp(),
        });
      });
      await batch.commit();
    } catch (err) {
      console.error("Failed to update status:", err);
      alert("Failed to update status.");
    }
  };

  const openChat = (lead) => {
    queueLeadChat(lead);
    if (setParentActiveTab) setParentActiveTab("dealer-chat");
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    setStatusFilter("all");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setStatusFilter("all");
  };

  const directLeads = leads.filter((l) => l.leadType === "direct");
  const nearbyLeads = leads.filter((l) => l.leadType === "nearby");

  const currentTabLeads = activeTab === "direct" ? directLeads : nearbyLeads;

  const filteredLeads = currentTabLeads.filter((l) => {
    const matchesSearch =
      (l.tenantName || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (l.propertyTitle || "").toLowerCase().includes(searchQuery.toLowerCase()) ||
      (l.tenantPhone || "").includes(searchQuery);

    const matchesStatus = statusFilter === "all" || l.status === statusFilter;

    return matchesSearch && matchesStatus;
  });

  const isFiltered = searchQuery !== "" || statusFilter !== "all";
  const tabTotal = currentTabLeads.length;
  const countText = isFiltered
    ? `${filteredLeads.length} of ${tabTotal} leads`
    : `${tabTotal} ${tabTotal === 1 ? "lead" : "leads"}`;

  const tableHead = (
    <thead>
      <tr>
        <th scope="col">Tenant</th>
        <th scope="col">Listing</th>
        <th scope="col">Channels</th>
        <th scope="col">Status</th>
        <th scope="col">Last activity</th>
        <th scope="col" className="dl-col-actions"><span className="sr-only">Actions</span></th>
      </tr>
    </thead>
  );

  let body;
  if (loading) {
    body = (
      <div className="table-wrap dl-table-wrap" aria-busy="true" aria-label="Loading leads">
        <table className="table dl-table">
          {tableHead}
          <tbody>
            {SKELETON_ROWS.map((i) => (
              <tr key={i} aria-hidden="true">
                <td className="dl-col-tenant">
                  <div className="dl-tenant">
                    <span className="skeleton dl-skel-avatar" />
                    <div className="dl-tenant-text">
                      <span className="skeleton dl-skel-line dl-skel-w-lg" />
                      <span className="skeleton dl-skel-line dl-skel-w-md" />
                    </div>
                  </div>
                </td>
                <td className="dl-col-listing">
                  <span className="skeleton dl-skel-line dl-skel-w-lg" />
                  <span className="skeleton dl-skel-line dl-skel-w-sm" />
                </td>
                <td className="dl-col-channels"><span className="skeleton dl-skel-chip" /></td>
                <td className="dl-col-status"><span className="skeleton dl-skel-chip" /></td>
                <td className="dl-col-time"><span className="skeleton dl-skel-line dl-skel-w-sm" /></td>
                <td className="dl-col-actions"><span className="skeleton dl-skel-actions" /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  } else if (tabTotal === 0) {
    body = (
      <div className="card">
        <div className="empty-state">
          <span className="empty-state-icon"><People size={24} variant="Linear" /></span>
          {activeTab === "direct" ? (
            <>
              <h3>No leads yet</h3>
              <p>When someone calls, chats or books a visit on one of your listings, the lead appears here.</p>
            </>
          ) : (
            <>
              <h3>No nearby leads yet</h3>
              <p>When a buyer enquires about a property near your location, the lead appears here.</p>
            </>
          )}
        </div>
      </div>
    );
  } else if (filteredLeads.length === 0) {
    body = (
      <div className="card">
        <div className="empty-state">
          <span className="empty-state-icon"><SearchNormal1 size={24} variant="Linear" /></span>
          <h3>No leads match</h3>
          <p>None of your {activeTab} leads match this search or status. Try another search, or clear the filters.</p>
          <button type="button" className="btn-secondary btn-sm" onClick={clearFilters}>Clear filters</button>
        </div>
      </div>
    );
  } else {
    body = (
      <div className="table-wrap dl-table-wrap">
        <table className="table dl-table">
          {tableHead}
          <tbody>
            {filteredLeads.map((lead) => {
              const name = lead.tenantName || "Client";
              const currentStatus = lead.status || "New";
              const channels = Array.isArray(lead.actions) && lead.actions.length > 0 ? lead.actions : [lead.type];
              const listingMeta = [
                getTypeLabel(lead.propertyType, lead.propertyCategory, true),
                getListingBadge(lead.category, lead.propertyCategory),
                lead.leadType === "nearby" ? `${lead.distanceKm?.toFixed(1) || 0} km away` : "",
              ].filter(Boolean).join(" · ");

              return (
                <tr
                  key={lead.id}
                  id={`lead-row-${lead.id}`}
                  className={highlightId === lead.id ? "is-focused" : undefined}
                >
                  <td className="dl-col-tenant">
                    <div className="dl-tenant">
                      <span className="avatar" aria-hidden="true">
                        {lead.tenantName?.charAt(0).toUpperCase() || "?"}
                      </span>
                      <div className="dl-tenant-text">
                        <span className="dl-tenant-name">{name}</span>
                        <span className="dl-tenant-phone">{lead.tenantPhone || "No contact"}</span>
                      </div>
                    </div>
                  </td>

                  <td className="dl-col-listing">
                    <span className="dl-listing-title" title={lead.propertyTitle}>
                      {lead.propertyTitle || "Direct lead inquiry"}
                    </span>
                    {listingMeta && <span className="dl-listing-meta">{listingMeta}</span>}
                  </td>

                  <td className="dl-col-channels">
                    <div className="dl-chips">
                      {channels.map((act) => (
                        <LeadChannelChip key={act || "enquiry"} type={act} />
                      ))}
                      {lead.interactionCount > 1 && (
                        <span className="chip lead-chip-quiet">{lead.interactionCount} interactions</span>
                      )}
                    </div>
                  </td>

                  <td className="dl-col-status">
                    <LeadStatusChip status={lead.status} />
                  </td>

                  <td className="dl-col-time">
                    <LeadTime date={leadActivityDate(lead)} />
                  </td>

                  <td className="dl-col-actions">
                    <div className="dl-actions">
                      <a
                        href={`tel:${lead.tenantPhone}`}
                        className="btn-icon dl-act"
                        aria-label={`Call ${name}`}
                        title="Call"
                      >
                        <Call size={18} variant="Linear" aria-hidden="true" />
                        <span className="dl-act-label">Call</span>
                      </a>
                      <button
                        type="button"
                        className="btn-icon dl-act"
                        aria-label={`Chat with ${name}`}
                        title="Chat"
                        onClick={() => openChat(lead)}
                      >
                        <Message size={18} variant="Linear" aria-hidden="true" />
                        <span className="dl-act-label">Chat</span>
                      </button>
                      <Select
                        className="dl-status-select"
                        ariaLabel={`Change status for ${name}`}
                        value=""
                        placeholder="Change status"
                        options={statusChoices(currentStatus)}
                        onChange={(status) => handleStatusChange(lead, status)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div className="dl-panel">
      <div className="toolbar dl-toolbar">
        <div className="segmented dl-source" role="group" aria-label="Lead source">
          <button
            type="button"
            aria-pressed={activeTab === "direct"}
            onClick={() => switchTab("direct")}
          >
            Direct
            <span className="dl-seg-count">{directLeads.length}</span>
          </button>
          <button
            type="button"
            aria-pressed={activeTab === "nearby"}
            onClick={() => switchTab("nearby")}
          >
            Nearby
            <span className="dl-seg-count">{nearbyLeads.length}</span>
          </button>
        </div>

        <div className="search-field dl-search">
          <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
          <input
            type="text"
            className="input"
            placeholder="Search tenant, phone or listing"
            aria-label="Search leads"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <Select
          className="dl-status-filter"
          ariaLabel="Filter by status"
          value={statusFilter}
          options={STATUS_FILTERS}
          onChange={setStatusFilter}
        />

        <span className="toolbar-spacer" />
        {!loading && <span className="dl-count" aria-live="polite">{countText}</span>}
      </div>

      {body}
    </div>
  );
}
